// Timeline / arranger: a synced track-headers column (DOM) + a canvas lane area
// that draws the ruler, clips with cached waveforms, the playhead, and loop region.
import { Store } from "../state/store";
import { AudioEngine } from "../audio/engine";
import { InputDevice, LivePeaks } from "../audio/recorder";
type LivePeaksAt = LivePeaks & { startSec: number };
import { el, secPerBeat, fmtBars, fmtTimecode } from "../util";
import { Track, Clip, clipSpeed } from "../state/model";
import { BASE_TRACK_HEIGHT, TAKE_ROW_HEIGHT } from "../state/model";
import { makeThumbnailVideo } from "../video/source";
import { openContextMenu, openModal, toast } from "./modal";
import { transitionsOf, DEFAULT_TRANSITION } from "../state/transitions";
import { generatorFrame, isAnimated } from "../video/generators";
import type { GeneratorSpec } from "../state/model";

const RULER_H = 28;
const MAX_PPS = 400; // finest zoom (px/sec) — enough for frame-precise edits
const clampH = (h: number) => Math.max(56, Math.min(400, h || 0)) || 120;
const clampW = (w: number) => Math.max(150, Math.min(520, w || 0)) || 220;
const THUMB_H = 90; // px height of cached thumbnail strip cells
const SNAP_PX = 8;  // magnetic snap distance to edit points
const FADE_BAND = 11; // px: top band of a clip where the fade handles live
const PYR_BIN = 256;  // samples per bin of the cached waveform pyramid
const XBAND = 16;     // px height of the transition band at the bottom of a lane

/** Min/max per PYR_BIN samples (both channels folded) — built once per buffer, so
 *  drawing a waveform at any zoom only touches the pixels on screen. */
interface Pyramid { min: Float32Array; max: Float32Array; }
/** Cached horizontal filmstrip for a video asset (content-stable; not pps-keyed). */
interface ThumbStrip { strip: HTMLCanvasElement; thumbW: number; count: number; dur: number; ready: boolean; }

export type TimelineTool = "select" | "blade" | "ripple" | "slip";
const TOOLS: { id: TimelineTool; icon: string; label: string; key: string; tip: string }[] = [
  { id: "select", icon: "↖", label: "Select", key: "V", tip: "Select / move / trim clips" },
  { id: "blade", icon: "✂", label: "Blade", key: "C", tip: "Click a clip to cut it (Shift+click cuts every track)" },
  { id: "ripple", icon: "⇤⇥", label: "Ripple", key: "B", tip: "Ripple trim: drag a clip edge and later clips on the track follow" },
  { id: "slip", icon: "⇆", label: "Slip", key: "Y", tip: "Slip: drag inside a clip to change which part of the media it shows, without moving it" },
];

type DragMode = "move" | "left" | "right" | "gain" | "fadein" | "fadeout" | "slip" | "rleft" | "rright" | "xdur";
type HitZone = "move" | "left" | "right" | "take" | "gain" | "fadein" | "fadeout";
type ClipSnap = { start: number; offset: number; duration: number };

export class Timeline {
  el = el("div", { class: "arranger" });
  private toolbar = el("div", { class: "tl-toolbar" });
  private body = el("div", { class: "tl-body" });
  // Log-mapped 0..1000: left = whole project fits, right = frame-precise.
  private zoomSlider = el("input", { attrs: { type: "range", min: "0", max: "1000", step: "1", title: "Timeline zoom — left fits the whole project, right is frame-precise (Ctrl+wheel, or = / −)" } }) as HTMLInputElement;
  private fitBtn = el("button", { class: "mini", text: "⤢ Fit", attrs: { title: "Zoom to fit the whole project (\\)" } }) as HTMLButtonElement;
  private trackHSlider = el("input", { attrs: { type: "range", min: "56", max: "400", step: "4", title: "Track height" } }) as HTMLInputElement;
  private toolBtns = new Map<TimelineTool, HTMLButtonElement>();
  private snapBtn = el("button", { class: "mini toggle", text: "🧲 Snap", attrs: { title: "Snapping (N): clips snap to edit points, the playhead and markers, then the grid. Hold Alt to invert while dragging." } }) as HTMLButtonElement;
  private rulerBtn = el("button", { class: "mini", attrs: { title: "Ruler/grid units: timecode + frames (video) or bars + beats (audio)" } }) as HTMLButtonElement;
  private headsEl = el("div", { class: "track-heads" });
  private wrapEl = el("div", { class: "timeline-wrap" });
  private canvas = document.createElement("canvas");
  private c2d = this.canvas.getContext("2d")!;
  private cache = document.createElement("canvas");
  private cc2d = this.cache.getContext("2d")!;
  private dirty = true;

  pps = 80;
  scrollX = 0; // px
  scrollY = 0; // px
  // Global track row height (vertical zoom), persisted; lanes + headers use it.
  trackHeight = clampH(Number(localStorage.getItem("webcut.trackHeight")) || BASE_TRACK_HEIGHT);
  private headsResizer = el("div", { class: "heads-resizer", attrs: { title: "Drag to resize the track header column" } });
  private devices: InputDevice[] = [];
  private pyramids = new WeakMap<AudioBuffer, Pyramid>();
  private thumbs = new Map<string, ThumbStrip>(); // assetId -> filmstrip (content-stable)
  /** Ruler/grid units: "time" = timecode + frames (video default), "bars" = beats. */
  rulerMode: "time" | "bars" = (localStorage.getItem("webcut.rulerMode") as any) || "time";
  tool: TimelineTool = "select";

  private drag: {
    clipId: string; mode: DragMode; startSec: number; grabX: number; startGain: number; grabY: number; laneH: number;
    starts: { id: string; startSec: number }[]; undone: boolean;
    points: number[];                    // edit points to snap to (dragged clips excluded)
    base?: Map<string, ClipSnap>;        // pre-drag state for ripple/slip (re-applied each move)
    rippleTracks?: Set<string>;
  } | null = null;
  private marquee: { x0: number; y0: number; x1: number; y1: number } | null = null;
  private pan: { startX: number; startScrollX: number; startY: number; startTop: number; moved: boolean; laneId: string | null; seekX: number } | null = null;
  private dragY = 0;
  private scrubbing = false;
  private lastFollowPos = -1;
  private snapLine: number | null = null; // time of the edit point a drag snapped to
  private hoverX: number | null = null;   // pointer x over the lanes (blade preview)

  /** Keep the playhead on screen: page-flip while playing, scroll-into-view on seek.
   *  Skips while the user is actively scrubbing/panning/dragging. */
  followPlayhead(pos: number, playing: boolean) {
    if (this.scrubbing || this.pan || this.drag || this.hdrag) return;
    if (playing) { this.scrollToTime(pos); return; }
    if (Math.abs(pos - this.lastFollowPos) > 1e-4) { this.scrollToTime(pos); this.lastFollowPos = pos; }
  }
  private hbar = el("div", { class: "h-scroll" });
  private hthumb = el("div", { class: "h-thumb" });
  private hdrag: { startX: number; startScroll: number } | null = null;
  private readonly EDGE = 7; // px hit zone for trim handles
  snap = localStorage.getItem("webcut.snap") !== "0"; // snapping on by default (Alt inverts)

  private snapActive(alt: boolean) { return this.snap !== alt; } // snap XOR alt

  /** Snap a time to the grid (frames in time mode, beats in bars mode). */
  private gridSnap(sec: number): number {
    if (this.rulerMode === "time") {
      const f = 1 / (this.store.project.fps || 30);
      return Math.round(sec / f) * f;
    }
    const spb = secPerBeat(this.store.project.tempo);
    return Math.round(sec / spb) * spb;
  }

  /** Nearest edit point within SNAP_PX of any candidate time → the shift to apply. */
  private magnet(cands: number[], points: number[]): { delta: number; at: number } | null {
    const pos = this.engine.position();
    const lim = SNAP_PX / this.pps;
    let best: { delta: number; at: number } | null = null;
    for (const t of cands) {
      for (const p of [...points, pos]) {
        const d = p - t;
        if (Math.abs(d) <= lim && (!best || Math.abs(d) < Math.abs(best.delta))) best = { delta: d, at: p };
      }
    }
    return best;
  }

  /** Snap a single time: edit points first, then the grid. Sets the snap guide. */
  private snapTime(sec: number, alt: boolean, points?: number[]): number {
    this.snapLine = null;
    if (!this.snapActive(alt)) return sec;
    const m = this.magnet([sec], points ?? this.store.editPoints());
    if (m) { this.snapLine = m.at; return m.at; }
    return this.gridSnap(sec);
  }

  setRulerMode(mode: "time" | "bars") {
    this.rulerMode = mode;
    localStorage.setItem("webcut.rulerMode", mode);
    this.rulerBtn.textContent = mode === "time" ? "⏱ TC" : "♪ Bars";
    this.invalidate();
  }
  toggleRulerMode() { this.setRulerMode(this.rulerMode === "time" ? "bars" : "time"); }
  setSnap(on: boolean) {
    this.snap = on;
    localStorage.setItem("webcut.snap", on ? "1" : "0");
    this.snapBtn.classList.toggle("on", on);
  }

  setTool(tool: TimelineTool) {
    this.tool = tool;
    for (const [id, b] of this.toolBtns) b.classList.toggle("on", id === tool);
    this.canvas.style.cursor = tool === "blade" ? "crosshair" : "";
    this.invalidate();
  }

  /** Y within a clip for its gain line (gain 2→top, 1→middle, 0→bottom). */
  private gainLineY(top: number, ch: number, gain: number): number {
    return top + (1 - Math.max(0, Math.min(1, gain / 2))) * ch;
  }

  constructor(
    private store: Store,
    private engine: AudioEngine,
    private cb: { onChange?: () => void; editClip?: (clipId: string) => void; editSpeed?: (clipId: string) => void; editTransition?: (clipId: string) => void; showShortcuts?: () => void } = {},
  ) {
    this.hbar.append(this.hthumb);
    this.wrapEl.append(this.canvas, this.hbar);
    // Tools, snapping, ruler units, zoom + track height live in the timeline's own toolbar.
    const toolGroup = el("div", { class: "tl-tools" });
    for (const t of TOOLS) {
      const b = el("button", { class: "mini tool", text: t.icon, attrs: { title: `${t.label} (${t.key}) — ${t.tip}` } }) as HTMLButtonElement;
      b.addEventListener("click", () => this.setTool(t.id));
      this.toolBtns.set(t.id, b);
      toolGroup.append(b);
    }
    this.snapBtn.addEventListener("click", () => this.setSnap(!this.snap));
    this.rulerBtn.addEventListener("click", () => this.toggleRulerMode());
    this.zoomSlider.value = String(this.sliderFromPps(this.pps));
    this.zoomSlider.addEventListener("input", () => this.setZoom(this.ppsFromSlider(Number(this.zoomSlider.value))));
    this.fitBtn.addEventListener("click", () => this.zoomToFit());
    this.trackHSlider.value = String(this.trackHeight);
    this.trackHSlider.addEventListener("input", () => this.setTrackHeight(Number(this.trackHSlider.value)));
    const helpBtn = el("button", { class: "mini", text: "⌨", attrs: { title: "Keyboard shortcuts (?)" } });
    helpBtn.addEventListener("click", () => this.cb.showShortcuts?.());
    this.toolbar.append(
      toolGroup, el("div", { class: "tl-sep" }),
      this.snapBtn, this.rulerBtn, el("div", { class: "tl-sep" }),
      el("span", { class: "hint", text: "🔍" }), this.zoomSlider, this.fitBtn,
      el("span", { class: "hint", text: "⇕" }), this.trackHSlider,
      el("div", { class: "spacer" }), helpBtn,
    );
    this.setTool("select");
    this.setSnap(this.snap);
    this.setRulerMode(this.rulerMode);
    this.body.append(this.headsEl, this.headsResizer, this.wrapEl);
    this.el.append(this.toolbar, this.body);
    const hw = Number(localStorage.getItem("webcut.headsWidth"));
    if (hw) this.headsEl.style.width = clampW(hw) + "px";
    this.bind();
    this.bindHScroll();
    this.bindHeadsResize();
    store.on("change", ({ scope }) => {
      if (scope === "tracks" || scope === "project" || scope === "assets") { this.invalidate(); this.rebuildHeads(); }
      if (scope === "transport") this.invalidate(); // loop on/off
      if (scope === "assets" || scope === "project") this.pruneThumbs();
      if (scope === "selection") this.markSelection();
    });
    store.on("selection", () => { this.markSelection(); this.invalidate(); });
  }

  setZoom(pps: number) {
    // Min zoom adapts to project length: you can zoom out until the whole
    // project fits the viewport, but no further. Max stays frame-precise.
    this.pps = Math.max(this.fitPps(), Math.min(MAX_PPS, pps));
    if (document.activeElement !== this.zoomSlider) this.zoomSlider.value = String(this.sliderFromPps(this.pps));
    this.invalidate();
  }

  /** Zoom by a factor keeping `anchor` (default: the playhead) at the same x. */
  zoomBy(factor: number, anchor = this.engine.position()) {
    const ax = this.secToX(anchor);
    const keep = ax >= 0 && ax <= this.wrapEl.clientWidth ? ax : this.wrapEl.clientWidth / 2;
    this.setZoom(this.pps * factor);
    this.scrollX = Math.max(0, anchor * this.pps - keep);
  }

  /** Pixels-per-second at which the entire project fits the timeline viewport.
   *  This is the effective minimum zoom, so it shrinks as the project grows. */
  private fitPps(): number {
    const w = (this.wrapEl.clientWidth || 800) - 24; // small right margin
    const len = Math.max(this.store.contentLength(), 1);
    return Math.max(0.02, Math.min(MAX_PPS, w / len));
  }

  /** Zoom so the whole project is visible, and scroll back to the start. */
  zoomToFit() {
    this.scrollX = 0;
    this.setZoom(this.fitPps());
  }

  // The zoom slider is log-mapped over [fitPps, MAX_PPS]: 0 = whole project, 1000 = frame-precise.
  private ppsFromSlider(v: number): number {
    const lo = this.fitPps(), hi = MAX_PPS;
    if (lo >= hi) return hi;
    return lo * Math.pow(hi / lo, Math.max(0, Math.min(1000, v)) / 1000);
  }
  private sliderFromPps(pps: number): number {
    const lo = this.fitPps(), hi = MAX_PPS;
    if (lo >= hi) return 1000;
    return Math.round(1000 * Math.log(Math.max(lo, pps) / lo) / Math.log(hi / lo));
  }

  /** Global track row height (vertical zoom). Headers + canvas lanes follow it. */
  setTrackHeight(px: number) {
    this.trackHeight = clampH(px);
    localStorage.setItem("webcut.trackHeight", String(this.trackHeight));
    this.rebuildHeads();
    this.invalidate();
  }

  /** Drag the divider on the right edge of the track-header column to resize it. */
  private bindHeadsResize() {
    this.headsResizer.addEventListener("mousedown", (e) => {
      e.preventDefault();
      const startX = e.clientX, startW = this.headsEl.offsetWidth;
      document.body.style.cursor = "col-resize";
      const move = (ev: MouseEvent) => {
        const w = clampW(startW + (ev.clientX - startX));
        this.headsEl.style.width = w + "px";
        localStorage.setItem("webcut.headsWidth", String(w));
        this.layout();
      };
      const up = () => { document.body.style.cursor = ""; window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
    });
  }

  mount() {
    // Build headers synchronously so the track list renders even if rAF is
    // throttled at startup (e.g. a backgrounded tab); rAF then fixes sizing.
    this.rebuildHeads(true);
    requestAnimationFrame(() => { this.layout(); this.rebuildHeads(true); });
    new ResizeObserver(() => this.layout()).observe(this.wrapEl);
  }
  setDevices(d: InputDevice[]) { this.devices = d; this.rebuildHeads(); }
  invalidate() { this.dirty = true; }

  private layout() {
    const dpr = window.devicePixelRatio || 1;
    const w = this.wrapEl.clientWidth, h = this.wrapEl.clientHeight;
    for (const cv of [this.canvas, this.cache]) {
      cv.width = Math.max(1, Math.floor(w * dpr));
      cv.height = Math.max(1, Math.floor(h * dpr));
      cv.style.width = w + "px"; cv.style.height = h + "px";
    }
    this.c2d.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.cc2d.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.invalidate();
  }

  // ---- track headers ---------------------------------------------------
  private headsSig = "";
  /** Rebuild the header DOM — skipped unless something a header shows changed
   *  (clip drags emit a change per mousemove; rebuilding then was pure waste). */
  rebuildHeads(force = false) {
    const tracks = this.store.project.tracks;
    const sig = JSON.stringify([this.trackHeight, this.devices.map((d) => d.deviceId + d.label), tracks.map((t) => [
      t.id, t.name, t.kind, t.color, t.mute, t.solo, t.armed, t.monitor, !!t.disabled, t.inputDeviceId, t.inputChannel ?? "1",
      this.displayHeight(t), !!t.compExpanded, new Set(t.clips.filter((c) => c.takeGroupId).map((c) => c.takeGroupId)).size,
    ])]);
    if (!force && sig === this.headsSig && this.headsInner) return;
    this.headsSig = sig;
    this.headsEl.innerHTML = "";
    const pad = el("div", { class: "ruler-pad", text: "Timeline" });
    this.headsEl.append(pad);
    // Native vertical scroll for the track list; the canvas lane area follows it.
    const inner = el("div", { class: "heads-scroll" });
    inner.addEventListener("scroll", () => { this.scrollY = inner.scrollTop; this.invalidate(); });
    this.headsEl.append(inner);
    let prevKind: Track["kind"] | null = null;
    for (const t of tracks) {
      const avDivider = prevKind === "video" && t.kind !== "video" ? " av-divider" : "";
      prevKind = t.kind;
      // Progressive density: drop the least-critical controls as the row shrinks
      // so the header never overflows (ingain → device → meter, in that order).
      const dh = this.displayHeight(t);
      // thresholds = each tier's intrinsic content height (+margin): full ~117,
      // th-m ~94 (no ingain), th-s ~65 (no ingain/device), th-xs name+buttons.
      const tier = dh < 67 ? " th-xs" : dh < 96 ? " th-s" : dh < 120 ? " th-m" : "";
      const head = el("div", { class: "track-head" + (t.id === this.store.selectedTrackId ? " sel" : "") + tier + avDivider });
      head.style.height = dh + "px";
      head.addEventListener("mousedown", (e) => {
        if ((e.target as HTMLElement).tagName === "INPUT" || (e.target as HTMLElement).tagName === "SELECT" || (e.target as HTMLElement).tagName === "BUTTON") return;
        this.store.select(t.id, null);
      });
      const sw = el("div", { class: "swatch" }); sw.style.background = t.color;
      const name = el("input", { class: "name", attrs: { value: t.name } }) as HTMLInputElement;
      name.value = t.name;
      name.addEventListener("change", () => this.store.updateTrack(t.id, { name: name.value }, "tracks"));

      const mkMini = (label: string, cls: string, on: boolean, fn: () => void, title?: string) => {
        const b = el("button", { class: `mini ${cls}` + (on ? ` on ${cls}` : ""), text: label });
        if (title) b.title = title;
        b.addEventListener("click", (e) => { e.stopPropagation(); fn(); });
        return b;
      };
      const isVideo = t.kind === "video";
      const row1 = el("div", { class: "row" }, [name]);
      // Video tracks are purely visual — no audio functionality at all (mute/solo,
      // record-arm/monitor, device, meter all live on audio lanes only).
      const row2 = isVideo
        ? el("div", { class: "row" }, [
            el("span", { class: "hint", text: "🎬 Video" }),
            mkMini("●", "r", !!t.armed, () => {
              const arm = !t.armed;
              // one camera-record target at a time: arming a video track disarms others
              if (arm) for (const o of this.store.project.tracks) if (o.kind === "video" && o.id !== t.id && o.armed) this.store.updateTrack(o.id, { armed: false }, "tracks");
              this.store.updateTrack(t.id, { armed: arm }, "tracks");
            }, "Arm for camera recording — the 🎥 button records onto this track"),
            mkMini(t.disabled ? "🚫" : "👁", "vis", !t.disabled,
              () => this.store.updateTrack(t.id, { disabled: !t.disabled }, "tracks"),
              t.disabled ? "Video disabled — click to show this track in the output" : "Disable this video track (hide from preview + export)"),
          ])
        : el("div", { class: "row" }, [
            mkMini("M", "m", t.mute, () => this.store.updateTrack(t.id, { mute: !t.mute }, "tracks"), "Mute this track"),
            mkMini("S", "s", t.solo, () => this.store.updateTrack(t.id, { solo: !t.solo }, "tracks"), "Solo this track (silence others)"),
            mkMini("R", "r", t.armed, () => this.store.updateTrack(t.id, { armed: !t.armed }, "tracks"), "Record-arm this track for recording"),
            mkMini("🎧", "mon", t.monitor, () => this.store.updateTrack(t.id, { monitor: !t.monitor }, "tracks"), "Input monitor — hear the live mic input through this track while armed (use headphones to avoid feedback)"),
          ]);
      const takeCount = new Set(t.clips.filter((c) => c.takeGroupId).map((c) => c.takeGroupId)).size;
      if (takeCount > 0) {
        const exp = el("button", { class: "mini exp" + (t.compExpanded ? " on" : ""), text: `⤢ Takes`, attrs: { title: "Show/hide alternate takes (comp lane)" } });
        exp.addEventListener("click", (e) => { e.stopPropagation(); this.store.toggleCompExpand(t.id); });
        row2.append(exp);
      }

      head.append(sw, row1, row2);

      if (!isVideo) {
        const dev = el("select") as HTMLSelectElement;
        dev.append(el("option", { text: "Default input", attrs: { value: "" } }));
        for (const d of this.devices) {
          const o = el("option", { text: d.label, attrs: { value: d.deviceId } }) as HTMLOptionElement;
          if (t.inputDeviceId === d.deviceId) o.selected = true;
          dev.append(o);
        }
        dev.addEventListener("change", () => this.store.updateTrack(t.id, { inputDeviceId: dev.value || null }, "tracks"));
        // which interface input(s) to record — mono In 1 by default (DAW convention)
        const chan = el("select", { class: "inchan", attrs: { title: "Input channel to record: mono In 1 (default for a mic), mono In 2, or Stereo (both inputs)" } }) as HTMLSelectElement;
        for (const [v, label] of [["1", "In 1"], ["2", "In 2"], ["stereo", "Stereo"]] as const) {
          const o = el("option", { text: label, attrs: { value: v } }) as HTMLOptionElement;
          if ((t.inputChannel ?? "1") === v) o.selected = true;
          chan.append(o);
        }
        chan.addEventListener("change", () => this.store.updateTrack(t.id, { inputChannel: chan.value as any }, "tracks"));
        const devRow = el("div", { class: "row devrow" }, [dev, chan]);

        const fill = el("i");
        const peakMark = el("b", { class: "peak" });
        const meter = el("div", { class: "headmeter" }, [fill, peakMark]);
        (head as any)._meter = fill;
        (head as any)._peakMark = peakMark;
        (head as any)._meterWrap = meter;

        head.append(devRow, meter);
      }
      (head as any)._trackId = t.id;
      inner.append(head);
    }
    this.headsInner = inner;
    inner.scrollTop = this.scrollY; // restore scroll position after rebuild
  }
  private headsInner: HTMLElement | null = null;

  private markSelection() {
    this.headsEl.querySelectorAll(".track-head").forEach((h) => {
      h.classList.toggle("sel", (h as any)._trackId === this.store.selectedTrackId);
    });
  }

  private headPeak = new Map<string, number>();
  private headClip = new Map<string, number>();
  updateMeters(levels: Record<string, number>) {
    if (!this.headsInner) return;
    const now = performance.now();
    for (const head of Array.from(this.headsInner.children) as any[]) {
      if (!head._meter) continue; // video lanes have no meter
      const id = head._trackId;
      const lvl = levels[id] ?? 0;
      const held = Math.max(lvl, (this.headPeak.get(id) ?? 0) * 0.985);
      if (lvl < 0.001 && held < 0.001 && head._lastLvl === 0) continue; // silent + already drawn as silent
      head._lastLvl = lvl < 0.001 && held < 0.001 ? 0 : 1;
      head._meter.style.width = Math.min(100, lvl * 100) + "%";
      this.headPeak.set(id, held);
      head._peakMark.style.left = Math.min(100, held * 100) + "%";
      if (lvl >= 0.99) this.headClip.set(id, now + 1200);
      head._meterWrap.classList.toggle("clip", now < (this.headClip.get(id) ?? 0));
    }
  }

  // ---- waveform peaks --------------------------------------------------
  private pyramid(buf: AudioBuffer): Pyramid {
    let p = this.pyramids.get(buf);
    if (p) return p;
    const n = Math.ceil(buf.length / PYR_BIN);
    const min = new Float32Array(n), max = new Float32Array(n);
    const chans = [buf.getChannelData(0)];
    if (buf.numberOfChannels > 1) chans.push(buf.getChannelData(1));
    for (let b = 0; b < n; b++) {
      const s = b * PYR_BIN, e = Math.min(buf.length, s + PYR_BIN);
      let mn = 1, mx = -1;
      for (const d of chans) for (let i = s; i < e; i++) { const v = d[i]; if (v < mn) mn = v; if (v > mx) mx = v; }
      min[b] = mn; max[b] = mx;
    }
    p = { min, max };
    this.pyramids.set(buf, p);
    return p;
  }

  /** Draw a clip's waveform over the visible columns only. */
  private drawWave(g: CanvasRenderingContext2D, clip: Clip, x: number, top: number, cw: number, ch: number, w: number, color: string) {
    const buf = this.store.buffers.get(clip.assetId);
    if (!buf) return;
    const pyr = this.pyramid(buf);
    const sr = buf.sampleRate, speed = clipSpeed(clip);
    const ch0 = buf.getChannelData(0);
    const mid = top + ch / 2, amp = ch / 2 - 3;
    const gain = clip.gain ?? 1; // scale the waveform by the clip's volume
    const yLo = top + 1, yHi = top + ch - 1; // clamp drawing to the clip box
    const samplesPerPx = (sr * speed) / this.pps;
    const px0 = Math.max(0, Math.floor(x)), px1 = Math.min(w, Math.ceil(x + cw));
    g.fillStyle = color;
    for (let px = px0; px < px1; px++) {
      const s0 = Math.floor((clip.offset + ((px - x) / this.pps) * speed) * sr);
      const s1 = Math.max(s0 + 1, Math.floor(s0 + samplesPerPx));
      if (s1 <= 0 || s0 >= buf.length) continue;
      let mn = 1, mx = -1;
      if (s1 - s0 >= PYR_BIN * 2) {
        const b1 = Math.min(pyr.min.length, Math.ceil(s1 / PYR_BIN));
        for (let b = Math.max(0, Math.floor(s0 / PYR_BIN)); b < b1; b++) { if (pyr.min[b] < mn) mn = pyr.min[b]; if (pyr.max[b] > mx) mx = pyr.max[b]; }
      } else {
        const e = Math.min(buf.length, s1);
        for (let i = Math.max(0, s0); i < e; i++) { const v = ch0[i]; if (v < mn) mn = v; if (v > mx) mx = v; }
      }
      if (mx < mn) continue;
      const yt = Math.max(yLo, mid - mx * amp * gain);
      const yb = Math.min(yHi, mid - mn * amp * gain);
      if (yb > yt) g.fillRect(px, yt, 1, Math.max(1, yb - yt));
    }
  }

  // ---- drawing ---------------------------------------------------------
  private secToX(sec: number) { return sec * this.pps - this.scrollX; }
  private xToSec(x: number) { return (x + this.scrollX) / this.pps; }

  /** Number of take-alternate rows to show in a track's drawer when expanded. */
  private drawerRows(track: Track): number {
    if (!track.compExpanded) return 0;
    const counts = new Map<string, number>();
    for (const c of track.clips) if (c.takeGroupId) counts.set(c.takeGroupId, (counts.get(c.takeGroupId) ?? 0) + 1);
    let maxAlt = 0;
    for (const n of counts.values()) maxAlt = Math.max(maxAlt, n - 1);
    return Math.max(track.compExpanded && counts.size ? 1 : 0, maxAlt);
  }
  displayHeight(track: Track): number {
    return this.trackHeight + this.drawerRows(track) * TAKE_ROW_HEIGHT;
  }

  private laneTops(): { id: string; top: number; h: number; baseH: number; rows: number }[] {
    let y = RULER_H - this.scrollY;
    return this.store.project.tracks.map((t) => {
      const rows = this.drawerRows(t);
      const h = this.trackHeight + rows * TAKE_ROW_HEIGHT;
      const r = { id: t.id, top: y, h, baseH: this.trackHeight, rows };
      y += h; return r;
    });
  }

  /** Inactive take members of a group, in stable order, mapped to drawer rows. */
  private takeRow(track: Track, clip: { id: string; takeGroupId?: string }): number {
    if (!clip.takeGroupId) return 0;
    const alts = track.clips.filter((c) => c.takeGroupId === clip.takeGroupId && c.takeActive === false);
    return alts.findIndex((c) => c.id === clip.id);
  }

  private rebuildCache() {
    const w = this.wrapEl.clientWidth, h = this.wrapEl.clientHeight;
    const g = this.cc2d;
    g.clearRect(0, 0, w, h);
    g.fillStyle = "#0b0d12"; g.fillRect(0, 0, w, h);

    const p = this.store.project;
    const spb = secPerBeat(p.tempo);
    const barSec = spb * p.beatsPerBar;
    const startSec = this.xToSec(0), endSec = this.xToSec(w);
    const timeMode = this.rulerMode === "time";

    // lane backgrounds + grid
    const lanes = this.laneTops();
    const trks = p.tracks;
    lanes.forEach((lane, i) => {
      g.fillStyle = trks[i]?.kind === "video" ? "#0f1219" : "#0e1118";
      g.fillRect(0, lane.top, w, lane.h - 1);
      if (trks[i]?.id === this.store.selectedTrackId) { g.fillStyle = "rgba(91,140,255,0.04)"; g.fillRect(0, lane.top, w, lane.h - 1); }
    });
    // A/V divider: a thin accent line between the video and audio lane sections.
    for (let i = 1; i < lanes.length; i++) {
      if (trks[i - 1]?.kind === "video" && trks[i]?.kind !== "video") {
        g.fillStyle = "#5b8cff"; g.fillRect(0, lanes[i].top - 1, w, 2);
        break;
      }
    }
    // grid lines: time intervals (video) or beats/bars (audio)
    if (timeMode) {
      const step = this.timeGridInterval();
      const major = step * 5; // emphasize every 5th line
      for (let i = Math.floor(startSec / step); i * step < endSec; i++) {
        const t = i * step, x = this.secToX(t);
        const isMajor = Math.abs(t / major - Math.round(t / major)) < 1e-6;
        g.strokeStyle = isMajor ? "#232a38" : "#161b25";
        g.beginPath(); g.moveTo(x + 0.5, RULER_H); g.lineTo(x + 0.5, h); g.stroke();
      }
    } else {
      for (let b = Math.floor(startSec / spb); b * spb < endSec; b++) {
        const x = this.secToX(b * spb);
        const isBar = b % p.beatsPerBar === 0;
        g.strokeStyle = isBar ? "#232a38" : "#161b25";
        g.beginPath(); g.moveTo(x + 0.5, RULER_H); g.lineTo(x + 0.5, h); g.stroke();
      }
    }

    // loop: mark the range the loop button repeats (In->Out if set, else the
    // whole program) with a blue bar along the ruler
    const loopR = p.loopEnabled ? this.store.loopRange() : null;
    if (loopR) {
      const x0 = this.secToX(loopR.start), x1 = this.secToX(loopR.end);
      g.fillStyle = "#5b8cff";
      g.fillRect(x0, RULER_H - 10, Math.max(2, x1 - x0), 3);
    }
    // In/Out range (Lift/Extract)
    const mi = p.markIn, mo = p.markOut;
    if (mi != null || mo != null) {
      const a = this.secToX(mi ?? 0), b = mo != null ? this.secToX(mo) : w;
      g.fillStyle = "rgba(234,179,8,0.06)";
      g.fillRect(a, RULER_H, b - a, h - RULER_H);
    }

    // clips
    for (const lane of lanes) {
      const track = this.store.track(lane.id)!;
      // active clips in the main band
      for (const clip of track.clips) {
        if (clip.takeActive === false) continue;
        const x = this.secToX(clip.start), cw = clip.duration * this.pps;
        if (x + cw < 0 || x > w) continue;
        const selected = this.store.isClipSelected(clip.id);
        this.drawClip(g, x, lane.top + 2, cw, lane.baseH - 6, clip, track, selected, false, selected, w);
      }
      // transitions sit on the cut, in a band along the bottom of the lane
      for (const tw of transitionsOf(track.clips)) {
        const x0 = this.secToX(tw.t0), x1 = this.secToX(tw.t1);
        if (x1 < 0 || x0 > w) continue;
        this.drawTransition(g, x0, x1, this.transitionBandTop(lane), XBAND, track.kind === "video");
      }
      // take drawer (alternates)
      if (lane.rows > 0) {
        g.fillStyle = "#090b10";
        g.fillRect(0, lane.top + lane.baseH, w, lane.h - lane.baseH);
        for (const clip of track.clips) {
          if (clip.takeActive !== false || !clip.takeGroupId) continue;
          const row = this.takeRow(track, clip);
          const y = lane.top + lane.baseH + row * TAKE_ROW_HEIGHT;
          const x = this.secToX(clip.start), cw = clip.duration * this.pps;
          if (x + cw < 0 || x > w) continue;
          this.drawClip(g, x, y + 2, cw, TAKE_ROW_HEIGHT - 4, clip, track, false, true, false, w);
        }
      }
    }

    // ruler
    g.fillStyle = "#11151d"; g.fillRect(0, 0, w, RULER_H);
    g.strokeStyle = "#2a3140"; g.beginPath(); g.moveTo(0, RULER_H + .5); g.lineTo(w, RULER_H + .5); g.stroke();
    g.fillStyle = "#8a95a6"; g.font = "10px Segoe UI";
    if (timeMode) {
      // label every other grid line with timecode so labels don't crowd
      const step = this.timeGridInterval();
      const labelStep = step * 2 * this.pps < 52 ? step * 5 : step * 2;
      for (let i = Math.ceil(startSec / labelStep); i * labelStep < endSec; i++) {
        const t = i * labelStep, x = this.secToX(t);
        g.strokeStyle = "#2a3140"; g.beginPath(); g.moveTo(x + .5, 14); g.lineTo(x + .5, RULER_H); g.stroke();
        g.fillText(fmtTimecode(t, p.fps), x + 4, 12);
      }
    } else {
      for (let bar = Math.floor(startSec / barSec); bar * barSec < endSec; bar++) {
        const x = this.secToX(bar * barSec);
        g.strokeStyle = "#2a3140"; g.beginPath(); g.moveTo(x + .5, 14); g.lineTo(x + .5, RULER_H); g.stroke();
        g.fillText(String(bar + 1), x + 4, 12);
      }
    }
    // In/Out brackets on the ruler
    if (mi != null || mo != null) {
      const a = this.secToX(mi ?? 0), b = mo != null ? this.secToX(mo) : w;
      g.fillStyle = "rgba(234,179,8,0.28)"; g.fillRect(a, RULER_H - 6, b - a, 6);
      g.fillStyle = "#eab308";
      if (mi != null) { g.fillRect(a, 14, 2, RULER_H - 14); g.fillRect(a, RULER_H - 2, 6, 2); }
      if (mo != null) { g.fillRect(b - 2, 14, 2, RULER_H - 14); g.fillRect(b - 6, RULER_H - 2, 6, 2); }
    }
    // markers: a flag on the ruler + a faint line down through the lanes
    for (const m of p.markers ?? []) {
      const x = this.secToX(m.time);
      if (x < -60 || x > w + 2) continue;
      g.strokeStyle = "rgba(244,114,182,0.35)"; g.lineWidth = 1;
      g.beginPath(); g.moveTo(x + 0.5, RULER_H); g.lineTo(x + 0.5, h); g.stroke();
      g.fillStyle = "#f472b6";
      g.beginPath(); g.moveTo(x - 5, 15); g.lineTo(x + 5, 15); g.lineTo(x + 5, 22); g.lineTo(x, RULER_H - 1); g.lineTo(x - 5, 22); g.fill();
      g.font = "10px Segoe UI"; g.fillStyle = "#f9a8d4";
      g.fillText(m.name, x + 7, 24);
    }
    this.dirty = false;
  }

  /** Pick a "nice" grid interval (seconds) so lines are at least ~64px apart.
   *  Candidates step from sub-second frame multiples up to hours. */
  private timeGridInterval(): number {
    const fps = this.store.project.fps || 30;
    const fr = 1 / fps;
    const candidates = [fr, 2 * fr, 5 * fr, 10 * fr, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600];
    const minPx = 64;
    for (const c of candidates) if (c * this.pps >= minPx) return c;
    return candidates[candidates.length - 1];
  }

  private drawClip(
    g: CanvasRenderingContext2D, x: number, top: number, cw: number, ch: number,
    clip: Clip, track: Track, selected: boolean, dim: boolean, handles: boolean, w: number,
  ) {
    const off = !!clip.disabled;
    g.globalAlpha = dim ? 0.55 : off ? 0.4 : 1;
    g.fillStyle = track.color + (dim ? "22" : "33");
    g.fillRect(x, top, cw, ch);
    g.strokeStyle = selected ? "#ffffff" : track.color;
    g.lineWidth = selected ? 2 : 1;
    g.strokeRect(x + 0.5, top + 0.5, cw - 1, ch - 1);
    if (track.kind === "video") this.drawVideoStrip(g, x, top, cw, ch, clip);
    else this.drawWave(g, clip, x, top, cw, ch, w, track.color);

    // fade ramps: shade the faded corner and draw the ramp line
    const fadeMin = track.kind === "video" ? 0.02 : 0.03;
    const fi = clip.fadeIn >= fadeMin ? Math.min(cw, clip.fadeIn * this.pps) : 0;
    const fo = clip.fadeOut >= fadeMin ? Math.min(cw, clip.fadeOut * this.pps) : 0;
    if (fi > 1 || fo > 1) {
      g.fillStyle = "rgba(0,0,0,0.35)";
      g.strokeStyle = "rgba(255,255,255,0.8)"; g.lineWidth = 1;
      if (fi > 1) {
        g.beginPath(); g.moveTo(x, top); g.lineTo(x + fi, top); g.lineTo(x, top + ch); g.closePath(); g.fill();
        g.beginPath(); g.moveTo(x, top + ch); g.lineTo(x + fi, top); g.stroke();
      }
      if (fo > 1) {
        const r = x + cw;
        g.beginPath(); g.moveTo(r, top); g.lineTo(r - fo, top); g.lineTo(r, top + ch); g.closePath(); g.fill();
        g.beginPath(); g.moveTo(r - fo, top); g.lineTo(r, top + ch); g.stroke();
      }
    }

    // title strip: name, speed and state badges
    g.fillStyle = "#dfe6f0"; g.font = "11px Segoe UI";
    g.save(); g.beginPath(); g.rect(x, top, cw, 14); g.clip();
    const sp = clipSpeed(clip);
    const tags = (off ? "⊘ " : "") + (sp !== 1 ? `${+sp.toFixed(2)}× ` : "") + (clip.linkId ? "🔗 " : "");
    g.fillText(tags + clip.name, x + 5, top + 11); g.restore();
    if (off) { // diagonal hatch over disabled clips
      g.save(); g.beginPath(); g.rect(x, top, cw, ch); g.clip();
      g.strokeStyle = "rgba(255,255,255,0.18)"; g.lineWidth = 1;
      for (let k = -ch; k < cw; k += 8) { g.beginPath(); g.moveTo(x + k, top + ch); g.lineTo(x + k + ch, top); g.stroke(); }
      g.restore();
    }
    // fade handles (top corners) — drag inward to fade
    if (!dim && cw > 26) {
      g.fillStyle = selected ? "#ffffff" : "rgba(255,255,255,0.55)";
      g.fillRect(x + Math.max(2, fi) - 3, top + 1, 6, 6);
      g.fillRect(x + cw - Math.max(2, fo) - 3, top + 1, 6, 6);
    }
    if (handles) {
      g.fillStyle = "#ffffff";
      g.fillRect(x, top + FADE_BAND, 3, ch - FADE_BAND);
      g.fillRect(x + cw - 3, top + FADE_BAND, 3, ch - FADE_BAND);
      // gain line (drag vertically to change clip volume) — audio clips only
      if (track.kind !== "video") {
        const gy = this.gainLineY(top, ch, clip.gain ?? 1);
        g.strokeStyle = "rgba(255,255,255,0.7)"; g.lineWidth = 1.5;
        g.setLineDash([4, 3]);
        g.beginPath(); g.moveTo(x + 3, gy); g.lineTo(x + cw - 3, gy); g.stroke();
        g.setLineDash([]);
      }
    }
    g.globalAlpha = 1;
  }

  private transitionBandTop(lane: { top: number; baseH: number }) { return lane.top + 2 + (lane.baseH - 6) - XBAND - 1; }

  /** A transition: a bow-tie box across the cut, labelled when there's room. */
  private drawTransition(g: CanvasRenderingContext2D, x0: number, x1: number, top: number, h: number, video: boolean) {
    const bw = Math.max(6, x1 - x0);
    g.fillStyle = video ? "rgba(155,107,255,0.85)" : "rgba(34,197,94,0.8)";
    g.fillRect(x0, top, bw, h);
    g.strokeStyle = "rgba(255,255,255,0.9)"; g.lineWidth = 1;
    g.strokeRect(x0 + 0.5, top + 0.5, bw - 1, h - 1);
    g.beginPath(); g.moveTo(x0, top + h); g.lineTo(x0 + bw, top); g.moveTo(x0, top); g.lineTo(x0 + bw, top + h); g.stroke();
    if (bw > 84) {
      const label = video ? "Cross Dissolve" : "Crossfade";
      g.font = "bold 10px Segoe UI";
      const tw = g.measureText(label).width + 8, lx = x0 + (bw - tw) / 2;
      g.fillStyle = "rgba(20,16,36,0.85)"; g.fillRect(lx, top + 2, tw, h - 4);
      g.fillStyle = "#fff"; g.fillText(label, lx + 4, top + h - 4);
    }
  }

  /** Transition under a canvas point (its band across the cut), if any. */
  private transitionHit(x: number, y: number): { trackId: string; clipId: string } | null {
    for (const lane of this.laneTops()) {
      const top = this.transitionBandTop(lane);
      if (y < top || y > top + XBAND) continue;
      const track = this.store.track(lane.id)!;
      for (const tw of transitionsOf(track.clips)) {
        const x0 = this.secToX(tw.t0), x1 = Math.max(x0 + 6, this.secToX(tw.t1));
        if (x >= x0 - 2 && x <= x1 + 2) return { trackId: lane.id, clipId: tw.b.id };
      }
    }
    return null;
  }

  // ---- video thumbnails ------------------------------------------------
  /** Draw a video clip's filmstrip: stretch the cached strip's source slice
   *  [offset, offset+span] into the clip box (respects trims + speed). */
  private drawVideoStrip(g: CanvasRenderingContext2D, x: number, top: number, cw: number, ch: number, clip: Clip) {
    const gen = this.store.asset(clip.assetId)?.generator;
    if (gen) { this.drawGeneratedStrip(g, x, top, cw, ch, clip, gen); return; }
    const ts = this.getThumbs(clip.assetId);
    if (!ts || !ts.ready || ts.strip.width === 0) return; // placeholder = the color fill already drawn
    const dur = ts.dur || 1;
    const span = clip.duration * clipSpeed(clip);
    const sx0 = Math.max(0, (clip.offset / dur) * ts.strip.width);
    const sx1 = Math.min(ts.strip.width, ((clip.offset + span) / dur) * ts.strip.width);
    const sw = Math.max(1, sx1 - sx0);
    g.save();
    g.beginPath(); g.rect(x, top + 14, cw, ch - 14); g.clip(); // leave the name strip clear
    try { g.drawImage(ts.strip, sx0, 0, sw, ts.strip.height, x, top + 14, cw, ch - 14); } catch {}
    g.restore();
  }

  /** Cached filmstrip for a video asset, generating it lazily on first use. */
  /** Generated media: tile small rendered frames across the visible part of the
   *  clip (each tile shows the frame at its own time, so a countdown counts down). */
  private drawGeneratedStrip(g: CanvasRenderingContext2D, x: number, top: number, cw: number, ch: number, clip: Clip, gen: GeneratorSpec) {
    const p = this.store.project;
    const th = Math.max(8, ch - 14), tw = Math.max(8, Math.round(th * (p.width / p.height)));
    const w = this.wrapEl.clientWidth;
    g.save();
    g.beginPath(); g.rect(x, top + 14, cw, ch - 14); g.clip();
    const i0 = Math.max(0, Math.floor(-x / tw)), i1 = Math.ceil(Math.min(cw, w - x) / tw);
    for (let i = i0; i < i1; i++) {
      const t = clip.offset + ((i * tw) / this.pps) * clipSpeed(clip);
      const cv = generatorFrame(`thumb|${clip.assetId}|${isAnimated(gen) ? i % 64 : 0}`, gen, tw, th, t);
      g.drawImage(cv, x + i * tw, top + 14);
    }
    g.restore();
  }

  private getThumbs(assetId: string): ThumbStrip | null {
    const existing = this.thumbs.get(assetId);
    if (existing) return existing;
    const asset = this.store.asset(assetId), src = this.store.video(assetId);
    if (!asset || asset.kind !== "video" || !src) return null;
    // Reserve immediately so we only generate once.
    const entry: ThumbStrip = { strip: document.createElement("canvas"), thumbW: 0, count: 0, dur: asset.duration || src.duration || 1, ready: false };
    this.thumbs.set(assetId, entry);
    this.genThumbs(assetId, entry).catch((e) => console.warn("thumbnail gen failed", e));
    return entry;
  }

  /** Drop filmstrips of assets that were removed from the project. */
  private pruneThumbs() {
    for (const id of this.thumbs.keys()) if (!this.store.asset(id)) this.thumbs.delete(id);
  }

  private async genThumbs(assetId: string, entry: ThumbStrip) {
    const asset = this.store.asset(assetId), src = this.store.video(assetId);
    if (!asset || !src) return;
    const dur = entry.dur;
    const aspect = (asset.width || 16) / (asset.height || 9);
    const thumbW = Math.max(8, Math.round(THUMB_H * aspect));
    const count = Math.max(2, Math.min(40, Math.round(dur)));
    entry.thumbW = thumbW; entry.count = count;
    entry.strip.width = thumbW * count; entry.strip.height = THUMB_H;
    const g = entry.strip.getContext("2d")!;
    const v = makeThumbnailVideo(src.url);
    await new Promise<void>((res) => { if (v.readyState >= 1) res(); else { v.addEventListener("loadedmetadata", () => res(), { once: true }); v.addEventListener("error", () => res(), { once: true }); } });
    const seek = (t: number) => new Promise<void>((res) => {
      const done = () => { clearTimeout(to); v.removeEventListener("seeked", done); res(); };
      const to = setTimeout(done, 3000); // a stuck seek must not stall the strip
      v.addEventListener("seeked", done);
      try { v.currentTime = Math.max(0, Math.min(dur - 0.01, t)); } catch { done(); }
    });
    for (let i = 0; i < count; i++) {
      await seek(((i + 0.5) / count) * dur);
      try { g.drawImage(v, i * thumbW, 0, thumbW, THUMB_H); } catch {}
      // show the strip progressively instead of waiting for every frame
      if (i === 0 || i % 8 === 7) { entry.ready = true; this.invalidate(); }
    }
    v.removeAttribute("src"); try { v.load(); } catch {}
    entry.ready = true;
    this.invalidate();
  }

  private contentWidth(): number {
    return Math.max(this.wrapEl.clientWidth, this.store.contentLength() * this.pps + 200);
  }

  private bindHScroll() {
    this.hthumb.addEventListener("mousedown", (e) => {
      e.stopPropagation(); e.preventDefault();
      this.hdrag = { startX: e.clientX, startScroll: this.scrollX };
    });
    this.hbar.addEventListener("mousedown", (e) => {
      if (e.target !== this.hbar) return; // clicked the track, not the thumb → page
      const w = this.wrapEl.clientWidth, content = this.contentWidth();
      const frac = e.offsetX / w;
      this.scrollX = Math.max(0, Math.min(content - w, frac * content - w / 2));
      this.invalidate();
    });
    window.addEventListener("mousemove", (e) => {
      if (!this.hdrag) return;
      const w = this.wrapEl.clientWidth, content = this.contentWidth();
      const thumbW = Math.max(30, (w / content) * w);
      const maxLeft = w - thumbW;
      if (maxLeft <= 0) return;
      const dx = e.clientX - this.hdrag.startX;
      this.scrollX = Math.max(0, Math.min(content - w, this.hdrag.startScroll + (dx / maxLeft) * (content - w)));
      this.invalidate();
    });
    window.addEventListener("mouseup", () => { this.hdrag = null; });
  }

  private updateHScroll() {
    const w = this.wrapEl.clientWidth, content = this.contentWidth();
    if (content <= w + 1) { this.hbar.style.display = "none"; return; }
    const clamped = Math.max(0, Math.min(this.scrollX, content - w));
    if (clamped !== this.scrollX) { this.scrollX = clamped; this.invalidate(); }
    const thumbW = Math.max(30, (w / content) * w);
    const maxLeft = w - thumbW;
    this.hbar.style.display = "block";
    this.hthumb.style.width = thumbW + "px";
    this.hthumb.style.left = (content > w ? (this.scrollX / (content - w)) * maxLeft : 0) + "px";
  }

  private recordOverlay: { sampleRate: number; tracks: Record<string, LivePeaksAt> } | null = null;
  setRecordOverlay(o: { sampleRate: number; tracks: Record<string, LivePeaksAt> } | null) { this.recordOverlay = o; }

  // Camera-recording indicator: a red box on a video track that grows in real time.
  private videoRecOverlay: { trackId: string; startSec: number; since: number } | null = null;
  setVideoRecordOverlay(o: { trackId: string; startSec: number; since: number } | null) { this.videoRecOverlay = o; }

  private drawVideoRecOverlay(g: CanvasRenderingContext2D, w: number) {
    const ov = this.videoRecOverlay;
    if (!ov) return;
    const lane = this.laneTops().find((l) => l.id === ov.trackId);
    if (!lane) return;
    const elapsed = (performance.now() - ov.since) / 1000;
    const x = this.secToX(ov.startSec);
    const bw = Math.max(2, elapsed * this.pps);
    const top = lane.top + 2, ch = lane.baseH - 6;
    g.fillStyle = "rgba(255,77,77,0.16)";
    g.fillRect(x, top, bw, ch);
    g.strokeStyle = "#ff4d4d"; g.lineWidth = 1.5;
    g.strokeRect(x + 0.5, top + 0.5, bw - 1, ch - 1);
    g.fillStyle = "#ff4d4d"; g.font = "bold 11px Segoe UI";
    if (x < w && x + bw > 0) g.fillText(`● REC ${Math.floor(elapsed / 60)}:${String(Math.floor(elapsed % 60)).padStart(2, "0")}`, Math.max(x + 5, 5), top + 13);
  }

  private drawRecordOverlay(g: CanvasRenderingContext2D, w: number) {
    const ov = this.recordOverlay;
    if (!ov) return;
    const lanes = this.laneTops();
    for (const lane of lanes) {
      const pk = ov.tracks[lane.id];
      if (!pk) continue;
      const startX = this.secToX(pk.startSec);
      const binPx = (pk.binSamples / ov.sampleRate) * this.pps;
      const n = pk.min.length;
      const lenPx = n * binPx;
      const top = lane.top + 2, ch = lane.baseH - 6;
      const mid = top + ch / 2, amp = ch / 2 - 3;
      // recording region box
      g.fillStyle = "rgba(255,77,77,0.12)";
      g.fillRect(startX, top, Math.max(2, lenPx), ch);
      g.strokeStyle = "#ff4d4d"; g.lineWidth = 1;
      g.strokeRect(startX + 0.5, top + 0.5, Math.max(2, lenPx) - 1, ch - 1);
      // waveform (bounded to visible columns; reduces bins-per-pixel when zoomed out)
      g.fillStyle = "#ff9a9a";
      const yLo = top + 1, yHi = top + ch - 1; // clamp drawing to the box
      if (binPx >= 1) {
        const i0 = Math.max(0, Math.floor((0 - startX) / binPx));
        const i1 = Math.min(n, Math.ceil((w - startX) / binPx));
        for (let i = i0; i < i1; i++) {
          const xx = startX + i * binPx;
          const yt = Math.max(yLo, mid - pk.max[i] * amp), yb = Math.min(yHi, mid - pk.min[i] * amp);
          if (yb > yt) g.fillRect(xx, yt, Math.max(1, binPx), Math.max(1, yb - yt));
        }
      } else {
        const px0 = Math.max(0, Math.floor(startX));
        const px1 = Math.min(w, Math.ceil(startX + lenPx));
        for (let px = px0; px < px1; px++) {
          const b0 = Math.max(0, Math.floor((px - startX) / binPx));
          const b1 = Math.min(n, Math.floor((px + 1 - startX) / binPx));
          let mn = 1, mx = -1;
          for (let b = b0; b < b1; b++) { if (pk.min[b] < mn) mn = pk.min[b]; if (pk.max[b] > mx) mx = pk.max[b]; }
          if (mx >= mn) { const yt = Math.max(yLo, mid - mx * amp), yb = Math.min(yHi, mid - mn * amp); if (yb > yt) g.fillRect(px, yt, 1, Math.max(1, yb - yt)); }
        }
      }
      // ● REC label
      g.fillStyle = "#ff4d4d"; g.font = "bold 10px Segoe UI";
      g.fillText("● REC", startX + 4, top + 11);
    }
  }

  /** While dragging clips, draw translucent ghosts that follow the cursor
   *  vertically and highlight the same-kind lane they'll drop onto. */
  private drawDragGhost(g: CanvasRenderingContext2D, w: number) {
    if (!this.drag || this.drag.mode !== "move" || !this.drag.undone) return;
    const dy = this.dragY - this.drag.grabY;
    if (Math.abs(dy) < 4) return; // pure horizontal moves don't need a ghost
    const lanes = this.laneTops();
    const gf = this.store.findClip(this.drag.clipId);
    const gKind = gf?.track.kind;

    // target-lane highlight (a same-kind lane, or the spill zones)
    const firstTop = lanes[0]?.top ?? RULER_H;
    const lastBottom = lanes.length ? lanes[lanes.length - 1].top + lanes[lanes.length - 1].h : RULER_H;
    let hi: { top: number; h: number } | null = null;
    if (this.dragY < firstTop) { if (gKind === "video") hi = { top: Math.max(RULER_H, firstTop - 6), h: 6 }; }
    else if (this.dragY > lastBottom) { if (gKind !== "video") hi = { top: lastBottom, h: 6 }; }
    else { const lane = lanes.find((l) => this.dragY >= l.top && this.dragY < l.top + l.h); if (lane && this.store.track(lane.id)?.kind === gKind) hi = { top: lane.top, h: lane.baseH }; }
    if (hi) { g.fillStyle = "rgba(91,140,255,0.14)"; g.fillRect(0, hi.top, w, hi.h); g.strokeStyle = "#5b8cff"; g.lineWidth = 1; g.strokeRect(0.5, hi.top + 0.5, w - 1, hi.h - 1); }

    // ghosts of the dragged clips, offset by the vertical drag delta
    g.globalAlpha = 0.55;
    for (const s of this.drag.starts) {
      const f = this.store.findClip(s.id);
      if (!f) continue;
      const lane = lanes.find((l) => l.id === f.track.id);
      if (!lane) continue;
      const x = this.secToX(f.clip.start), cw = f.clip.duration * this.pps;
      if (x + cw < 0 || x > w) continue;
      g.fillStyle = f.track.color + "55";
      g.fillRect(x, lane.top + 2 + dy, cw, lane.baseH - 6);
      g.strokeStyle = "#ffffff"; g.lineWidth = 1.5;
      g.strokeRect(x + 0.5, lane.top + 2 + dy + 0.5, cw - 1, lane.baseH - 6 - 1);
    }
    g.globalAlpha = 1;
  }

  draw() {
    this.updateHScroll();
    // keep the zoom slider in sync with Ctrl+wheel zooming (and with project-length changes)
    if (document.activeElement !== this.zoomSlider) {
      const sv = String(this.sliderFromPps(this.pps));
      if (this.zoomSlider.value !== sv) this.zoomSlider.value = sv;
    }
    if (this.dirty) this.rebuildCache();
    const w = this.wrapEl.clientWidth, h = this.wrapEl.clientHeight;
    const g = this.c2d;
    g.clearRect(0, 0, w, h);
    g.drawImage(this.cache, 0, 0, this.cache.width, this.cache.height, 0, 0, w, h);
    this.drawRecordOverlay(g, w);
    this.drawVideoRecOverlay(g, w);
    this.drawDragGhost(g, w);
    if (this.marquee) {
      const m = this.marquee;
      const rx = Math.min(m.x0, m.x1), ry = Math.min(m.y0, m.y1);
      const rw = Math.abs(m.x1 - m.x0), rh = Math.abs(m.y1 - m.y0);
      g.fillStyle = "rgba(91,140,255,0.18)"; g.fillRect(rx, ry, rw, rh);
      g.strokeStyle = "#5b8cff"; g.lineWidth = 1; g.strokeRect(rx + 0.5, ry + 0.5, rw, rh);
    }
    // snap guide while dragging
    if (this.drag && this.snapLine != null) {
      const sx = this.secToX(this.snapLine);
      g.strokeStyle = "#facc15"; g.lineWidth = 1; g.setLineDash([3, 3]);
      g.beginPath(); g.moveTo(sx + 0.5, RULER_H); g.lineTo(sx + 0.5, h); g.stroke(); g.setLineDash([]);
    }
    // blade preview: the cut line under the pointer
    if (this.tool === "blade" && this.hoverX != null && !this.drag) {
      const t = this.snapTime(this.xToSec(this.hoverX), false);
      const bx = this.secToX(t);
      g.strokeStyle = "#ff9f43"; g.lineWidth = 1;
      g.beginPath(); g.moveTo(bx + 0.5, RULER_H); g.lineTo(bx + 0.5, h); g.stroke();
      this.snapLine = null;
    }
    // playhead line (only where visible)
    const pos = this.engine.position();
    const px = this.secToX(pos);
    if (px >= 0 && px <= w) {
      g.strokeStyle = "#ff4d4d"; g.lineWidth = 1.5;
      g.beginPath(); g.moveTo(px, RULER_H); g.lineTo(px, h); g.stroke();
    }
    // Always-visible playhead position: a timecode bubble on the ruler that sticks
    // to the nearest edge when the playhead is scrolled off-screen.
    const offscreen = px < 0 || px > w;
    const label = fmtTimecode(pos, this.store.project.fps);
    g.font = "bold 11px Segoe UI"; g.textAlign = "left";
    const lw = g.measureText(label).width + 12;
    const lx = Math.max(0, Math.min(w - lw, px - lw / 2));
    g.fillStyle = offscreen ? "#8a2f2f" : "#ff4d4d";
    g.fillRect(lx, 0, lw, RULER_H - 2);
    if (!offscreen) { g.beginPath(); g.moveTo(px - 5, RULER_H - 2); g.lineTo(px + 5, RULER_H - 2); g.lineTo(px, RULER_H + 4); g.fill(); }
    else { // little arrow toward the off-screen playhead
      const ax = px < 0 ? lx + 3 : lx + lw - 3, dir = px < 0 ? -1 : 1;
      g.beginPath(); g.moveTo(ax, 4); g.lineTo(ax, RULER_H - 6); g.lineTo(ax + dir * 5, (RULER_H - 2) / 2); g.fill();
    }
    g.fillStyle = "#fff";
    g.fillText(label, lx + 6, 13);
  }

  // ---- interaction -----------------------------------------------------
  /** Marker under a ruler x position (within 6px), if any. */
  private markerAt(x: number) {
    return (this.store.project.markers ?? []).find((m) => Math.abs(this.secToX(m.time) - x) <= 6);
  }

  private renameMarker(id: string) {
    const m = this.store.project.markers?.find((x) => x.id === id);
    if (!m) return;
    const inp = el("input", { attrs: { value: m.name } }) as HTMLInputElement;
    inp.value = m.name; inp.style.width = "100%";
    openModal("Rename marker", inp, [
      { label: "Delete", onClick: () => this.store.removeMarker(id) },
      { label: "Save", primary: true, onClick: () => this.store.updateMarker(id, { name: inp.value || m.name }) },
    ]);
    setTimeout(() => { inp.focus(); inp.select(); }, 0);
    inp.addEventListener("keydown", (e) => { if (e.key === "Enter") { this.store.updateMarker(id, { name: inp.value || m.name }); document.querySelector(".modal-bg")?.remove(); } });
  }

  private bind() {
    this.wrapEl.addEventListener("wheel", (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const before = this.xToSec(e.offsetX);
        this.pps = Math.max(this.fitPps(), Math.min(MAX_PPS, this.pps * (e.deltaY < 0 ? 1.15 : 0.87)));
        this.scrollX = Math.max(0, before * this.pps - e.offsetX);
      } else if (e.shiftKey) {
        this.scrollX = Math.max(0, this.scrollX + e.deltaY);
      } else {
        this.scrollX = Math.max(0, this.scrollX + e.deltaX);
        // Drive the native track-list scrollbar; its scroll handler updates scrollY.
        if (this.headsInner) this.headsInner.scrollTop += e.deltaY;
        else this.scrollY = Math.max(0, this.scrollY + e.deltaY);
      }
      this.invalidate();
    }, { passive: false });

    this.canvas.addEventListener("mouseleave", () => { this.hoverX = null; });
    this.canvas.addEventListener("mousemove", (e) => {
      this.hoverX = e.offsetY >= RULER_H ? e.offsetX : null;
      if (this.drag || this.pan) return;
      if (e.offsetY < RULER_H) { this.canvas.style.cursor = this.markerAt(e.offsetX) ? "pointer" : "ew-resize"; return; }
      if (this.tool !== "blade" && this.transitionHit(e.offsetX, e.offsetY)) { this.canvas.style.cursor = "ew-resize"; return; }
      const hit = this.hitAt(e.offsetX, e.offsetY);
      if (this.tool === "blade") { this.canvas.style.cursor = hit ? "crosshair" : "grab"; return; }
      if (this.tool === "slip" && hit?.zone === "move") { this.canvas.style.cursor = "col-resize"; return; }
      this.canvas.style.cursor = hit?.zone === "left" || hit?.zone === "right" ? (this.tool === "ripple" ? "w-resize" : "ew-resize")
        : hit?.zone === "fadein" || hit?.zone === "fadeout" ? "nesw-resize"
        : hit?.zone === "gain" ? "ns-resize"
        : hit?.zone === "take" ? "pointer"
        : "grab"; // a clip body = grab to move; empty area = grab to pan
    });

    this.canvas.addEventListener("mousedown", (e) => {
      if (e.button !== 0) return;
      const x = e.offsetX, y = e.offsetY;
      if (y < RULER_H) { this.scrubbing = true; this.engine.seek(Math.max(0, this.xToSec(x))); return; }
      // Transition band: drag sideways to change its length (symmetric around the cut).
      const th = this.tool !== "blade" ? this.transitionHit(x, y) : null;
      if (th) {
        const b = this.store.findClip(th.clipId)!.clip;
        this.drag = { clipId: th.clipId, mode: "xdur", startSec: b.start, grabX: x, startGain: 1, grabY: y, laneH: 0, starts: [], undone: false, points: [] };
        return;
      }
      const hit = this.hitAt(x, y);
      if (hit?.zone === "take") { this.store.setActiveTake(hit.clipId); return; }

      // Blade: cut the clicked clip (Shift = every track) at the pointer.
      if (this.tool === "blade" && hit) {
        const t = this.snapTime(this.xToSec(x), e.altKey);
        this.snapLine = null;
        if (e.shiftKey) {
          const ids = this.store.project.tracks.flatMap((tr) => tr.clips.filter((c) => c.takeActive !== false && t > c.start && t < c.start + c.duration).map((c) => c.id));
          this.store.splitClipsAt(t, ids);
        } else this.store.splitClipAt(hit.clipId, t);
        return;
      }

      if (hit) {
        const additive = e.shiftKey || e.ctrlKey || e.metaKey;
        if (additive) this.store.toggleClipSelection(hit.clipId);
        else if (!this.store.isClipSelected(hit.clipId)) this.store.select(hit.trackId, hit.clipId);
        else { this.store.selectedClipId = hit.clipId; this.store.selectedTrackId = hit.trackId; }
        // Only start a drag if the grabbed clip is (still) selected.
        if (this.store.isClipSelected(hit.clipId)) {
          const clip = this.store.findClip(hit.clipId)!.clip;
          const lane = this.laneTops().find((l) => l.id === hit.trackId)!;
          // Move linked partners (e.g. a video clip's audio) together with the selection.
          const moveIds = this.store.expandLinked(this.store.selectedClipList().map(({ clip }) => clip.id));
          const starts = moveIds.map((id) => ({ id, startSec: this.store.findClip(id)!.clip.start }));
          let mode: DragMode = hit.zone as DragMode;
          if (this.tool === "ripple" && (hit.zone === "left" || hit.zone === "right")) mode = hit.zone === "left" ? "rleft" : "rright";
          if (this.tool === "slip" && hit.zone === "move") mode = "slip";
          const exclude = new Set(mode === "move" ? moveIds : this.store.expandLinked([hit.clipId]));
          this.drag = {
            clipId: hit.clipId, mode, startSec: clip.start, grabX: x, startGain: clip.gain ?? 1, grabY: y, laneH: lane.baseH - 6,
            starts, undone: false, points: this.store.editPoints(exclude),
          };
          if (mode === "rleft" || mode === "rright" || mode === "slip") this.captureBase(hit.clipId, mode !== "slip");
        }
      } else {
        // Empty area: Shift-drag = marquee select; plain drag = pan; click = seek.
        if (e.shiftKey) {
          this.marquee = { x0: x, y0: y, x1: x, y1: y };
        } else {
          const lane = this.laneAt(y);
          this.pan = { startX: x, startScrollX: this.scrollX, startY: y, startTop: this.headsInner?.scrollTop ?? 0, moved: false, laneId: lane, seekX: x };
        }
      }
    });

    this.canvas.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      const x = e.offsetX, y = e.offsetY;
      const at = Math.max(0, this.xToSec(x));
      // Ruler: markers + In/Out.
      if (y < RULER_H) {
        const mk = this.markerAt(x);
        if (mk) {
          openContextMenu(e.clientX, e.clientY, [
            { label: `Rename “${mk.name}”…`, onClick: () => this.renameMarker(mk.id) },
            { label: "Go to marker", onClick: () => this.engine.seek(mk.time) },
            { label: "Delete marker", onClick: () => this.store.removeMarker(mk.id) },
          ]);
          return;
        }
        const p = this.store.project;
        openContextMenu(e.clientX, e.clientY, [
          { label: "Add marker here", onClick: () => this.store.addMarker(at) },
          { label: "Set In here", onClick: () => this.store.setMark("in", at) },
          { label: "Set Out here", onClick: () => this.store.setMark("out", at) },
          ...(p.markIn != null || p.markOut != null ? [{ label: "Clear In/Out", onClick: () => { this.store.setMark("in", undefined); this.store.setMark("out", undefined); } }] : []),
        ]);
        return;
      }
      const th = this.transitionHit(x, y);
      if (th) {
        const b = this.store.findClip(th.clipId)!.clip;
        openContextMenu(e.clientX, e.clientY, [
          { label: `Transition duration… (${(b.transitionIn ?? 0).toFixed(2)}s)`, onClick: () => this.cb.editTransition?.(th.clipId) },
          { label: "Go to cut", onClick: () => this.engine.seek(b.start) },
          { label: "Remove transition", onClick: () => this.store.setTransition(th.clipId, undefined) },
        ]);
        return;
      }
      const hit = this.hitAt(x, y);
      if (!hit) {
        // Empty lane area.
        const laneId = this.laneAt(y);
        const items = [
          ...(this.store.hasClipboard() ? [{ label: "Paste here", onClick: () => this.store.pasteClips(at) }] : []),
          ...(laneId ? [{ label: "Close gap", onClick: () => { if (!this.store.closeGapAt(laneId, at)) toast("No gap here to close."); } }] : []),
          ...(laneId ? [{ label: "Select clips to the right (this track)", onClick: () => this.store.selectForward(at, laneId) }] : []),
          { label: "Select clips to the right (all tracks)", onClick: () => this.store.selectForward(at) },
          { label: "Add marker here", onClick: () => this.store.addMarker(at) },
        ];
        openContextMenu(e.clientX, e.clientY, items);
        return;
      }
      // If the clicked clip isn't part of the selection, select just it.
      if (!this.store.isClipSelected(hit.clipId)) this.store.select(hit.trackId, hit.clipId);
      const track = this.store.track(hit.trackId)!;
      const clip = this.store.findClip(hit.clipId)?.clip;
      const inGroup = !!clip?.takeGroupId;
      const isLinked = !!clip?.linkId;
      const n = this.store.selectedClipIds.size;
      const sfx = n > 1 ? ` (${n})` : "";
      const pos = this.engine.position();
      const underPlayhead = !!clip && pos > clip.start && pos < clip.start + clip.duration;
      // Transitions at this clip's cuts (needs a clip abutting on the same track).
      const xName = track.kind === "video" ? "cross-dissolve" : "crossfade";
      const before = this.store.neighbor(hit.clipId, "before"), after = this.store.neighbor(hit.clipId, "after");
      const xItems = [
        ...(before && !clip?.transitionIn ? [{ label: `Add ${xName} at start`, onClick: () => this.store.setTransition(hit.clipId, DEFAULT_TRANSITION) }] : []),
        ...(before && clip?.transitionIn ? [{ label: `Remove ${xName} at start`, onClick: () => this.store.setTransition(hit.clipId, undefined) }] : []),
        ...(after && !after.transitionIn ? [{ label: `Add ${xName} at end`, onClick: () => this.store.setTransition(after.id, DEFAULT_TRANSITION) }] : []),
        ...(after && after.transitionIn ? [{ label: `Remove ${xName} at end`, onClick: () => this.store.setTransition(after.id, undefined) }] : []),
      ];
      openContextMenu(e.clientX, e.clientY, [
        ...xItems,
        { label: "Clip settings…" + sfx, onClick: () => this.cb.editClip?.(hit.clipId) },
        { label: `Speed / duration… (${+clipSpeed(clip ?? { }).toFixed(2)}×)`, onClick: () => this.cb.editSpeed?.(hit.clipId) },
        { label: (clip?.disabled ? "Enable clip" : "Disable clip") + sfx, onClick: () => this.store.toggleClipsEnabled() },
        { label: "Split at playhead" + sfx, onClick: () => this.store.splitClipsAt(pos), disabled: !underPlayhead },
        { label: "Trim start to playhead", onClick: () => this.store.trimToTime(pos, "start", false, [hit.clipId]), disabled: !underPlayhead },
        { label: "Trim end to playhead", onClick: () => this.store.trimToTime(pos, "end", false, [hit.clipId]), disabled: !underPlayhead },
        { label: "Copy" + sfx, onClick: () => this.store.copyClips() },
        { label: "Cut" + sfx, onClick: () => { this.store.copyClips(); this.store.removeClips(); } },
        { label: "Duplicate" + sfx, onClick: () => this.store.duplicateClips() },
        ...(hit.zone === "take" ? [{ label: "Promote take", onClick: () => this.store.setActiveTake(hit.clipId) }] : []),
        ...(inGroup ? [{ label: track.compExpanded ? "Collapse takes" : "Expand takes", onClick: () => this.store.toggleCompExpand(hit.trackId) }] : []),
        ...(isLinked ? [{ label: "Unlink A/V", onClick: () => this.store.unlinkClips() }] : []),
        ...(n > 1 && !isLinked ? [{ label: "Link selected", onClick: () => this.store.linkClips() }] : []),
        { label: "Select clips to the right", onClick: () => this.store.selectForward(clip?.start ?? at, hit.trackId) },
        { label: "Ripple delete" + sfx, onClick: () => this.store.rippleDelete() },
        { label: "Delete" + sfx, onClick: () => this.store.removeClips() },
      ]);
    });

    // Double-click a clip to open its settings; a marker to rename it.
    this.canvas.addEventListener("dblclick", (e) => {
      if (e.offsetY < RULER_H) { const mk = this.markerAt(e.offsetX); if (mk) this.renameMarker(mk.id); return; }
      if (this.tool === "blade") return;
      const th = this.transitionHit(e.offsetX, e.offsetY);
      if (th) { this.cb.editTransition?.(th.clipId); return; }
      const hit = this.hitAt(e.offsetX, e.offsetY);
      if (hit) {
        if (!this.store.isClipSelected(hit.clipId)) this.store.select(hit.trackId, hit.clipId);
        else this.store.selectedClipId = hit.clipId;
        this.cb.editClip?.(hit.clipId);
      }
    });

    window.addEventListener("mousemove", (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left, y = e.clientY - rect.top;
      if (this.scrubbing) { this.engine.seek(Math.max(0, this.xToSec(x))); return; }
      if (this.marquee) { this.marquee.x1 = x; this.marquee.y1 = y; return; }
      if (this.pan) {
        const dx = x - this.pan.startX, dy = y - this.pan.startY;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this.pan.moved = true;
        this.scrollX = Math.max(0, this.pan.startScrollX - dx);
        if (this.headsInner) this.headsInner.scrollTop = this.pan.startTop - dy;
        this.invalidate();
        return;
      }
      if (!this.drag) return;
      const d = this.drag;
      this.dragY = y;
      if (!d.undone) {
        if (Math.abs(x - d.grabX) < 2 && Math.abs(y - d.grabY) < 2) return; // ignore sub-pixel jitter on click
        this.store.pushUndo(); d.undone = true; // lazy: only on real movement
      }
      const alt = e.altKey;
      if (d.mode === "move") {
        // Snap the group's edges to edit points (else the grabbed clip to the grid),
        // then shift the whole selection by the same delta. (Cross-track placement
        // is resolved on drop — see mouseup.)
        const grabbed = this.store.findClip(d.clipId)?.clip;
        let ns = Math.max(0, d.startSec + (x - d.grabX) / this.pps);
        this.snapLine = null;
        if (this.snapActive(alt) && grabbed) {
          const m = this.magnet([ns, ns + grabbed.duration], d.points);
          if (m) { ns += m.delta; this.snapLine = m.at; } else ns = this.gridSnap(ns);
        }
        let delta = ns - d.startSec;
        for (const s of d.starts) delta = Math.max(delta, -s.startSec); // nobody crosses 0
        for (const s of d.starts) { const f = this.store.findClip(s.id); if (f) f.clip.start = s.startSec + delta; }
        this.store.emitChange("tracks");
      } else if (d.mode === "gain") {
        // drag up = louder; full lane height spans gain 0..2 — only the grabbed clip,
        // so linked audio clips keep independent volumes.
        const g = Math.max(0, Math.min(2, d.startGain + ((d.grabY - y) / d.laneH) * 2));
        this.store.updateClip(d.clipId, { gain: g }, false);
      } else if (d.mode === "fadein" || d.mode === "fadeout") {
        const f = this.store.findClip(d.clipId);
        if (!f) return;
        const c = f.clip, t = this.xToSec(x);
        const len = d.mode === "fadein" ? t - c.start : c.start + c.duration - t;
        const v = Math.max(0, Math.min(c.duration / 2, len));
        this.store.updateClip(d.clipId, d.mode === "fadein" ? { fadeIn: v } : { fadeOut: v }, false);
      } else if (d.mode === "xdur") {
        // d.startSec is the cut; length = twice the pointer's distance from it, ≥ 2 frames
        const fr = 1 / (this.store.project.fps || 30);
        const len = Math.max(2 * fr, Math.round((2 * Math.abs(this.xToSec(x) - d.startSec)) / fr) * fr);
        this.store.setTransition(d.clipId, len, false);
      } else if (d.mode === "slip") {
        this.applySlip((x - d.grabX) / this.pps);
      } else if (d.mode === "rleft" || d.mode === "rright") {
        this.applyRipple(d.mode === "rleft" ? "left" : "right", this.snapTime(this.xToSec(x), alt, d.points));
      } else {
        this.store.trimClip(d.clipId, d.mode, this.snapTime(this.xToSec(x), alt, d.points));
      }
    });
    window.addEventListener("mouseup", () => {
      this.scrubbing = false;
      this.snapLine = null;
      if (this.marquee) { this.commitMarquee(); this.marquee = null; }
      if (this.drag?.mode === "move" && this.drag.undone) {
        // Resolve the vertical drop — only after a real drag (a click never moves a
        // clip's track). A clip can only land on a lane of its OWN kind: video on
        // video lanes (or a new top video track), audio on audio lanes (or a new
        // bottom track). Dropping on a wrong-kind lane changes nothing.
        const lanes = this.laneTops();
        const gf = this.store.findClip(this.drag.clipId);
        if (gf && lanes.length && Math.abs(this.dragY - this.drag.grabY) >= 4) {
          const gKind = gf.track.kind;
          const firstTop = lanes[0].top;
          const lastBottom = lanes[lanes.length - 1].top + lanes[lanes.length - 1].h;
          let targetTrackId: string | null = null;
          if (this.dragY < firstTop) {
            if (gKind === "video") targetTrackId = this.store.createTrack("video").id; // new top video
          } else if (this.dragY > lastBottom) {
            if (gKind !== "video") targetTrackId = this.store.createTrack(gKind).id;    // new bottom audio/inst
          } else {
            const lane = this.laneAt(this.dragY);
            if (lane && this.store.track(lane)?.kind === gKind) targetTrackId = lane;   // same-kind only
          }
          // Only the grabbed clip changes track — linked partners keep their own
          // tracks (cross-track linking applies only at initial placement). They
          // still moved together in time via the drag's `starts`.
          if (targetTrackId && targetTrackId !== gf.track.id) this.store.moveClipToTrack(this.drag.clipId, targetTrackId);
        }
      }
      this.drag = null;
      if (this.pan) {
        if (!this.pan.moved) { // treat as a click: seek + select the lane
          if (this.pan.laneId) this.store.select(this.pan.laneId, null);
          this.engine.seek(Math.max(0, this.xToSec(this.pan.seekX)));
        }
        this.pan = null;
      }
      this.invalidate();
    });
  }

  /** Remember the pre-drag state of the grabbed clip's link group (and, for a
   *  ripple trim, every clip on their tracks) so each mousemove re-applies the
   *  edit from scratch instead of accumulating error. */
  private captureBase(clipId: string, rippleTracks: boolean) {
    if (!this.drag) return;
    const base = new Map<string, ClipSnap>();
    const tracks = new Set<string>();
    for (const id of this.store.expandLinked([clipId])) {
      const f = this.store.findClip(id);
      if (f) tracks.add(f.track.id);
    }
    for (const t of this.store.project.tracks) {
      for (const c of t.clips) {
        if (rippleTracks ? tracks.has(t.id) : this.store.expandLinked([clipId]).includes(c.id)) base.set(c.id, { start: c.start, offset: c.offset, duration: c.duration });
      }
    }
    this.drag.base = base;
    this.drag.rippleTracks = tracks;
  }
  private restoreBase() {
    for (const [id, s] of this.drag?.base ?? []) { const f = this.store.findClip(id); if (f) Object.assign(f.clip, s); }
  }

  /** Slip: shift the media under the clip (and its link partners) by `dx`
   *  timeline seconds, clamped so every clip stays inside its source. */
  private applySlip(dx: number) {
    const d = this.drag;
    if (!d?.base) return;
    this.restoreBase();
    const group = this.store.expandLinked([d.clipId]).map((id) => this.store.findClip(id)?.clip).filter(Boolean) as Clip[];
    // Allowed range of dx (timeline s) where every clip's offset stays valid.
    let lo = -Infinity, hi = Infinity;
    for (const c of group) {
      const [rLo, rHi] = this.store.slipRange(c); // allowed change of offset, source s
      const sp = clipSpeed(c);
      // offset' = offset - dx*sp  ⇒  dx ∈ [-rHi/sp, -rLo/sp]
      lo = Math.max(lo, -rHi / sp); hi = Math.min(hi, -rLo / sp);
    }
    const v = Math.max(lo, Math.min(hi, dx));
    for (const c of group) c.offset = Math.max(0, c.offset - v * clipSpeed(c));
    this.store.emitChange("tracks");
  }

  /** Ripple trim: move one edge of the grabbed clip (+ link partners) and shift
   *  every later clip on their tracks by the same amount, closing/opening space. */
  private applyRipple(edge: "left" | "right", t: number) {
    const d = this.drag;
    if (!d?.base || !d.rippleTracks) return;
    this.restoreBase();
    const g = this.store.findClip(d.clipId)?.clip;
    if (!g) return;
    const sp = clipSpeed(g);
    const ids = new Set(this.store.expandLinked([d.clipId]));
    let delta: number; // change in the grabbed clip's duration (timeline s)
    if (edge === "right") {
      const maxDur = (this.store.bufferDuration(g.assetId) - g.offset) / sp;
      const nd = Math.max(0.02, Math.min(maxDur, t - g.start));
      delta = nd - g.duration;
    } else {
      // Ripple in-point: the clip keeps its start; its head is trimmed/extended.
      const cut = Math.max(-g.offset / sp, Math.min(g.duration - 0.02, t - g.start));
      delta = -cut;
    }
    for (const id of ids) {
      const f = this.store.findClip(id);
      if (!f) continue;
      const c = f.clip, csp = clipSpeed(c);
      const origEnd = c.start + c.duration;
      let dd: number;
      if (edge === "right") {
        const maxDur = (this.store.bufferDuration(c.assetId) - c.offset) / csp;
        const nd = Math.max(0.02, Math.min(maxDur, c.duration + delta));
        dd = nd - c.duration; c.duration = nd;
      } else {
        const cut = Math.max(-c.offset / csp, Math.min(c.duration - 0.02, -delta));
        c.offset += cut * csp; c.duration -= cut; dd = -cut;
      }
      // shift the rest of this track by this clip's duration change
      for (const o of f.track.clips) if (!ids.has(o.id) && o.start >= origEnd - 1e-6) o.start = Math.max(0, o.start + dd);
    }
    this.store.emitChange("tracks");
  }

  private commitMarquee() {
    const m = this.marquee!;
    const rx0 = Math.min(m.x0, m.x1), rx1 = Math.max(m.x0, m.x1);
    const ry0 = Math.min(m.y0, m.y1), ry1 = Math.max(m.y0, m.y1);
    const ids: string[] = [];
    for (const lane of this.laneTops()) {
      const track = this.store.track(lane.id)!;
      const top = lane.top + 2, bot = lane.top + lane.baseH;
      if (bot < ry0 || top > ry1) continue; // lane outside marquee vertically
      for (const clip of track.clips) {
        if (clip.takeActive === false) continue;
        const cx = this.secToX(clip.start), cw = clip.duration * this.pps;
        if (cx + cw >= rx0 && cx <= rx1) ids.push(clip.id); // horizontal overlap
      }
    }
    this.store.setClipSelection(ids);
  }

  private laneAt(y: number): string | null {
    for (const lane of this.laneTops()) if (y >= lane.top && y < lane.top + lane.h) return lane.id;
    return null;
  }
  private hitAt(x: number, y: number): { trackId: string; clipId: string; zone: HitZone } | null {
    for (const lane of this.laneTops()) {
      if (y < lane.top || y >= lane.top + lane.h) continue;
      const track = this.store.track(lane.id)!;
      const inBase = y < lane.top + lane.baseH;
      if (inBase) {
        // Topmost (latest-starting) clip wins where clips overlap, matching the draw order.
        const clips = track.clips.filter((c) => c.takeActive !== false);
        for (let i = clips.length - 1; i >= 0; i--) {
          const clip = clips[i];
          const cx = this.secToX(clip.start), cw = clip.duration * this.pps;
          if (x < cx || x > cx + cw) continue;
          const top = lane.top + 2;
          // fade handles live in the top band near each corner's fade point
          if (y < top + FADE_BAND && cw > 26) {
            const fiX = cx + Math.max(2, clip.fadeIn * this.pps), foX = cx + cw - Math.max(2, clip.fadeOut * this.pps);
            if (Math.abs(x - fiX) <= 6) return { trackId: lane.id, clipId: clip.id, zone: "fadein" };
            if (Math.abs(x - foX) <= 6) return { trackId: lane.id, clipId: clip.id, zone: "fadeout" };
          }
          if (x <= cx + this.EDGE) return { trackId: lane.id, clipId: clip.id, zone: "left" };
          if (x >= cx + cw - this.EDGE) return { trackId: lane.id, clipId: clip.id, zone: "right" };
          // near the dashed gain line → volume drag (audio clips only)
          if (track.kind !== "video" && this.tool === "select") {
            const gy = this.gainLineY(top, lane.baseH - 6, clip.gain ?? 1);
            if (Math.abs(y - gy) <= 5) return { trackId: lane.id, clipId: clip.id, zone: "gain" };
          }
          return { trackId: lane.id, clipId: clip.id, zone: "move" };
        }
      } else {
        // drawer take alternates
        const row = Math.floor((y - lane.top - lane.baseH) / TAKE_ROW_HEIGHT);
        for (const clip of track.clips) {
          if (clip.takeActive !== false || !clip.takeGroupId) continue;
          if (this.takeRow(track, clip) !== row) continue;
          const cx = this.secToX(clip.start), cw = clip.duration * this.pps;
          if (x >= cx && x <= cx + cw) return { trackId: lane.id, clipId: clip.id, zone: "take" };
        }
      }
    }
    return null;
  }

  scrollToTime(sec: number) {
    const x = this.secToX(sec);
    const w = this.wrapEl.clientWidth;
    if (x < 0 || x > w - 60) { this.scrollX = Math.max(0, sec * this.pps - w * 0.3); this.invalidate(); }
  }

  /** Map a viewport pointer position to a timeline time / track (for drops). */
  timeAtClientX(clientX: number): number {
    const rect = this.canvas.getBoundingClientRect();
    return Math.max(0, this.xToSec(clientX - rect.left));
  }
  trackAtClientY(clientY: number): string | null {
    const rect = this.canvas.getBoundingClientRect();
    return this.laneAt(clientY - rect.top);
  }

  zoomLabel() { return fmtBars(this.xToSec(this.wrapEl.clientWidth), this.store.project.tempo, this.store.project.beatsPerBar); }
}
